import { FC, forwardRef, ReactNode } from 'react'

import { Listbox } from '@headlessui/react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { useBackgroundContext } from '../BackgroundContext/index.js'
import { CheckIcon, SelectorIcon } from '../icons/index.js'
import { HTMLTribeProps } from '../types/index.js'
import {
  FloatingContextProvider,
  FloatingWrapper,
  useFloatingContext,
} from '../utils/floating/index.js'
import { runIfFn } from '../utils/index.js'
import { useInputColors } from '../utils/styles/index.js'
import { SelectProvider, useSelect } from './SelectContext.js'

export type SelectProps<T> = {
  value: T
  onChange: (newValue: T) => void
  className?: string
  children?: ReactNode
  disabled?: boolean
  placeholder?: string
  items?: Array<{
    value: string | number
    text: string | ReactNode
    avatar?: string
  }>
  invalid?: boolean
  name?: string
}

/**
 * The Select component is a component that allows users pick a value from predefined options.
 */
export const Select = <T,>(props: SelectProps<T>) => {
  const {
    children,
    className,
    value,
    placeholder,
    items,
    invalid = false,
    disabled = false,
    ...rest
  } = props

  return (
    <Listbox {...rest} disabled={disabled} value={value}>
      {renderProps => {
        let content
        if (items) {
          content = (
            <>
              <SelectButton placeholder={placeholder}>
                {items.find(item => value === item.value)?.text || null}
              </SelectButton>
              <SelectItems>
                {items.map(item => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.text}
                  </SelectItem>
                ))}
              </SelectItems>
            </>
          )
        } else {
          content = runIfFn(children, renderProps)
        }

        return (
          <div className={twMerge(clsx('relative isolate', className))}>
            <SelectProvider
              open={renderProps.open}
              invalid={invalid}
              disabled={disabled}
            >
              <FloatingContextProvider placement="bottom-start">
                {content}
              </FloatingContextProvider>
            </SelectProvider>
          </div>
        )
      }}
    </Listbox>
  )
}

export type SelectButtonProps = HTMLTribeProps<'button'> & {
  arrowsClassname?: string
}

const SelectButton: FC<SelectButtonProps> = props => {
  const { children, className, placeholder, arrowsClassname, ...rest } = props
  const { invalid, disabled } = useSelect()
  const { reference } = useFloatingContext()
  const { backgroundType } = useBackgroundContext()

  const {
    backgroundClsx,
    borderClsx,
    invalidClsx,
    disabledClsx,
    iconClsx,
    fontClsx,
  } = useInputColors({
    invalid,
    disabled,
  })

  const placeholderClsx = [
    !invalid && [
      backgroundType === 'main' &&
        'text-content-on-background-subdued opacity-60',
      backgroundType === 'surface' && 'text-content-subdued opacity-60',
      backgroundType === 'secondary' && 'text-content-primary opacity-60',
    ],
    invalid && 'text-action-destructive opacity-60',
  ]

  return (
    <div ref={reference}>
      <Listbox.Button
        className={twMerge(
          clsx(
            'relative w-full rounded-md shadow-sm ps-3 pe-10 py-2 text-start cursor-default',
            [
              'appearance-none',
              'text-base',
              fontClsx,
              backgroundClsx,
              borderClsx,
              [
                'focus:outline-none focus-visible:ring ',
                !invalid
                  ? 'focus-visible:ring-focused'
                  : 'focus-visible:ring-line-negative focus-visible:border-line-negative',
              ],
              invalid && invalidClsx,
              disabled && disabledClsx,
            ],
            className,
          ),
        )}
        {...rest}
      >
        {() => (
          <>
            <span className={clsx('block truncate')}>
              {children || (
                <div className={clsx(placeholderClsx)}>
                  {placeholder || '\u00A0'}
                </div>
              )}
            </span>
            <span
              className={clsx(
                'absolute inset-y-0 end-0 flex items-center pe-2 pointer-events-none',
                arrowsClassname,
              )}
            >
              <SelectorIcon
                className={clsx('w-5 h-5', iconClsx)}
                aria-hidden="true"
              />
            </span>
          </>
        )}
      </Listbox.Button>
    </div>
  )
}

interface SelectItemsRenderProps {
  open: boolean
}

export type SelectItemsProps = Omit<HTMLTribeProps<'ul'>, 'children'> & {
  children?: ReactNode | ((props: SelectItemsRenderProps) => ReactNode)
  noWrapper?: boolean
}

const SelectItems = forwardRef<HTMLUListElement, SelectItemsProps>(
  (props, ref) => {
    const { children, className, noWrapper, ...rest } = props

    const { open } = useSelect()

    if (noWrapper) {
      return (
        <Listbox.Options
          className={twMerge(
            clsx(
              'w-full min-w-[160px] text-content-subdued bg-surface shadow-lg max-h-60 rounded-md py-1 text-base border border-line-subdued overflow-auto',
              'focus-visible:ring-1 focus:outline-none',
              className,
            ),
          )}
          {...rest}
          ref={ref}
        >
          {renderProps => <>{runIfFn(children, renderProps)}</>}
        </Listbox.Options>
      )
    }

    return (
      <FloatingWrapper open={open} sameWidth>
        <Listbox.Options
          className={twMerge(
            clsx(
              'w-full min-w-[160px] text-content-subdued bg-surface shadow-lg max-h-60 rounded-md py-1 text-base border border-line-subdued overflow-auto',
              'focus-visible:ring-1 focus:outline-none',
              className,
            ),
          )}
          {...rest}
          ref={ref}
        >
          {renderProps => <>{runIfFn(children, renderProps)}</>}
        </Listbox.Options>
      </FloatingWrapper>
    )
  },
)

interface SelectItemRenderProps {
  selected: boolean
  active: boolean
}
export type SelectItemProps<T> = Omit<
  HTMLTribeProps<'li'>,
  'value' | 'children'
> & {
  value: T
  disabled?: boolean
  children?: ReactNode | ((props: SelectItemRenderProps) => ReactNode)
  className?: string
}

const SelectItem: FC<SelectItemProps<unknown>> = props => {
  const { value, children, disabled, className, ...rest } = props

  return (
    <Listbox.Option
      className={({ active }) =>
        twMerge(
          clsx(
            active
              ? 'text-content-on-primary bg-action-primary'
              : 'text-content',
            'cursor-default select-none relative py-2 ps-3 pe-9',
            className,
          ),
        )
      }
      value={value}
      disabled={disabled}
      {...rest}
    >
      {({ selected, active }) => (
        <>
          <span
            className={clsx(
              selected ? 'font-semibold' : 'font-normal',
              disabled ? 'opacity-50' : '',
              'block truncate',
            )}
          >
            {runIfFn(children, {
              selected,
              active,
            })}
          </span>

          {selected ? (
            <span
              className={clsx(
                active ? 'text-content-on-primary' : 'text-action-primary',
                'absolute inset-y-0 end-0 flex items-center pe-4',
              )}
            >
              <CheckIcon className="h-5 w-5" aria-hidden="true" />
            </span>
          ) : null}
        </>
      )}
    </Listbox.Option>
  )
}

Select.Button = SelectButton
Select.Items = SelectItems
Select.Item = SelectItem
